<script setup lang="ts">
import { computed } from "vue";

/**
 * The Carbdial logo mark.
 *
 * Paths are copied verbatim from docs/handover/icons/brand/*.svg -- do not
 * redraw them. Rendered as inline SVG rather than <img> so the mark inherits
 * `currentColor`; the component sets no colour of its own, the parent does
 * (`color: var(--clay-500)` in the header lockup).
 *
 * `gauge` is the approved MVP variant. The other two ship so the choice can
 * change without a refactor, but must not be used until the mark is signed off
 * (open point O-4 in docs/PRD.md).
 */
const props = withDefaults(
  defineProps<{
    size?: number;
    variant?: "gauge" | "drop" | "chevron";
    /** True when the wordmark sits beside it -- then the mark is decorative. */
    decorative?: boolean;
  }>(),
  { size: 26, variant: "gauge", decorative: true },
);

// Separate bindings rather than a spread object: Vue types aria-hidden as
// Booleanish, and a union of two attribute shapes does not satisfy that.
const label = computed(() => (props.decorative ? undefined : "Carbdial"));
const role = computed(() => (props.decorative ? undefined : "img"));
</script>

<template>
  <svg
    class="brandmark"
    :aria-hidden="decorative || undefined"
    :role="role"
    :aria-label="label"
    :width="size"
    :height="size"
    viewBox="0 0 48 48"
  >
    <template v-if="variant === 'gauge'">
      <circle
        cx="24"
        cy="24"
        r="16"
        fill="none"
        stroke="currentColor"
        stroke-width="6"
        stroke-linecap="round"
        stroke-dasharray="72 29"
        transform="rotate(-40 24 24)"
      />
      <circle
        cx="24"
        cy="24"
        r="4.5"
        fill="currentColor"
      />
    </template>

    <template v-else-if="variant === 'drop'">
      <path
        d="M24 6c7.5 8.2 12 14.4 12 20a12 12 0 0 1-24 0c0-5.6 4.5-11.8 12-20Z"
        fill="none"
        stroke="currentColor"
        stroke-width="5"
        stroke-linejoin="round"
      />
      <circle
        cx="24"
        cy="27"
        r="4.5"
        fill="currentColor"
      />
    </template>

    <template v-else>
      <path
        d="M8 28l16-14 16 14"
        fill="none"
        stroke="currentColor"
        stroke-width="6"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M8 40l16-14 16 14"
        fill="none"
        stroke="currentColor"
        stroke-width="6"
        stroke-linecap="round"
        stroke-linejoin="round"
        opacity=".35"
      />
    </template>
  </svg>
</template>

<style scoped>
.brandmark {
  display: block;
  flex: none;
}
</style>
